import { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { showError } from '@utils';
import { boardsServices } from '@services';
import { QUERY_KEYS } from 'constants/queryKeys';
import { useAssignees } from 'hooks/common';
import {
  getSellRequestsPayload,
  getSellRequestsQueryParams,
  normalizeSellRequestsTasks,
} from 'hooks/sellRequests/utils';
import {
  SELL_REQUESTS_BOARD,
  SELL_REQUESTS_PAGE_SIZE,
} from 'hooks/sellRequests/constants';
import { omit } from 'lodash-es';

export const useUserSellRequestsOperations = (userId) => {
  const router = useRouter();
  const [filterState, setFilterState] = useState({
    modalFiltersRules: undefined,
    modalConditions: undefined,
  });
  const { assigneesWithNone, isLoading: isLoadingAssignees } = useAssignees();

  const page = router.query?.page || 1;
  const pageSize = router.query?.pageSize || SELL_REQUESTS_PAGE_SIZE;
  const orderBy = router.query?.orderBy || 'createdAt';
  const order = router.query?.order || 'desc';
  const query = router.query?.query || '';
  const archived = router.query?.archived;
  const assignee = router.query?.assignee;
  const board = SELL_REQUESTS_BOARD;

  const KNOWN_QUERY_PARAMS = [
    'page',
    'pageSize',
    'orderBy',
    'order',
    'query',
    'archived',
    'assignee',
    'tab',
    'userId',
  ];

  const extraParams = useMemo(
    () => omit(router.query, KNOWN_QUERY_PARAMS),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [router.query]
  );

  const params = useMemo(
    () => ({
      page,
      pageSize: parseInt(pageSize, 10) || SELL_REQUESTS_PAGE_SIZE,
      orderBy,
      order,
      query,
      archived,
      assignee,
      ...extraParams,
    }),
    [page, pageSize, orderBy, order, query, archived, assignee, extraParams]
  );

  const cleanParams = useMemo(
    () =>
      getSellRequestsQueryParams({
        ...params,
      }),
    [params]
  );

  // Build payload with userId filter added
  const payload = useMemo(() => {
    const basePayload = getSellRequestsPayload({
      ...cleanParams,
      filters: filterState.modalFiltersRules,
      boardId: board.id,
      boardType: board.type,
    });

    // Add userId filter to the value array
    if (userId) {
      basePayload.value = [
        ...basePayload.value,
        {
          field: 'customFields.userId',
          type: 5, // ComparableRuleType
          operator: 0,
          value: userId,
        },
      ];
    }

    return basePayload;
  }, [
    cleanParams,
    board.id,
    board.type,
    userId,
    filterState.modalFiltersRules,
  ]);

  const sellRequestsResponse = useQuery(
    [
      QUERY_KEYS.GET_USER_SELL_REQUEST_KEY,
      userId,
      cleanParams,
      filterState.modalFiltersRules,
    ],
    () =>
      boardsServices.getBoardTasks({
        boardId: board.id,
        params: getSellRequestsQueryParams(cleanParams),
        payload,
      }),
    { enabled: !!userId && router.isReady }
  );

  const enums = useQuery(
    [QUERY_KEYS.GET_BOARDS_ENUMS, board.id],
    () => boardsServices.getEnumsPerBoardId(board.id),
    { enabled: router.isReady }
  );

  const sellRequestsTasks = useMemo(
    () => normalizeSellRequestsTasks(sellRequestsResponse?.data?.data || []),
    [sellRequestsResponse?.data?.data]
  );

  useEffect(() => {
    if (sellRequestsResponse.isError) {
      showError(sellRequestsResponse?.error);
    }
  }, [sellRequestsResponse.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (enums.isError) {
      showError(enums?.error);
    }
  }, [enums.isError]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFiltersSubmit = (rules) => {
    setFilterState((prev) => ({ ...prev, modalFiltersRules: rules }));
  };

  const handleFiltersChange = (conditions) => {
    setFilterState((prev) => ({
      ...prev,
      modalConditions: conditions,
      ...(conditions?.rules?.length ? {} : { modalFiltersRules: undefined }),
    }));
  };

  return {
    page,
    pageSize: parseInt(pageSize, 10) || SELL_REQUESTS_PAGE_SIZE,
    total: sellRequestsResponse?.data?.meta?.total,
    loading:
      sellRequestsResponse?.isLoading ||
      sellRequestsResponse?.isFetching ||
      isLoadingAssignees,
    error: sellRequestsResponse?.error,
    isSuccess: sellRequestsResponse?.isSuccess,
    sellRequests: sellRequestsTasks?.tasks,
    totalFinalPrice: sellRequestsTasks?.totalFinalPrice,
    assignees: assigneesWithNone,
    enums,
    refresh: sellRequestsResponse.refetch,
    onFiltersSubmit: handleFiltersSubmit,
    onFiltersChange: handleFiltersChange,
    filters: filterState,
  };
};

export default useUserSellRequestsOperations;
